import { z } from 'zod'

import {
  chartWidgetSchema,
  filterSchema,
  kpiWidgetSchema,
  listWidgetSchema,
  tableWidgetSchema,
} from '@/lib/cube-report'
import { RELATIVE_RANGE_PATTERN } from '@/lib/format'

/**
 * Rapor belgesi şablonu (`kind: "document"`, docs/REPORT-DOCUMENTS-PLAN.md §4) ve dönem
 * kopyası (`document_runs`) sözleşmesi (docs/contracts.md §12). İçerik ProseMirror
 * JSON'udur; düğüm seti KAPALIDIR: şemada olmayan düğüm, işaret ya da öznitelik hatadır.
 *
 * `schemas/report-definition.schema.json` bu şemadan üretilir (backend aynı dosyayı
 * doğrular); `superRefine` kuralları (sorgu başvuruları) JSON Schema'ya geçmez, yalnız
 * burada çalışır.
 */

/** TipTap `JSONContent`'in kapalı alt kümesi: doğrulanmış düğüm. */
export interface DocNode {
  type: string
  attrs?: Record<string, unknown>
  content?: DocNode[]
  marks?: { type: string; attrs?: Record<string, unknown> }[]
  text?: string
}

const IDENT = '[A-Za-z][A-Za-z0-9_]*'
/** Sorgu anahtarı ve ölçü/boyut adı. */
const identifier = z.string().regex(new RegExp(`^${IDENT}$`))
/** `sorgu.ölçü` ya da (yalnız `repeat` içinde) `row.ölçü`. */
const memberRef = z.string().regex(new RegExp(`^${IDENT}\\.${IDENT}$`))
/** Koşulun sağ tarafı: `sorgu.ölçü`, `prev.sorgu.ölçü` (önceki dönem) ya da sabit sayı. */
const operandRef = z.string().regex(new RegExp(`^(prev\\.)?${IDENT}\\.${IDENT}$`))

/**
 * `row` ve `prev` ile `period` ve `filters` başvuru önekidir; sorgu adı olamaz.
 * `period.label` ve `filters.<üye>` yalnız `value`'da geçerlidir (aşağıda).
 */
const RESERVED_QUERY_NAMES = ['row', 'prev', 'period', 'filters']

const view = z.string().min(1)

// ---------------------------------------------------------------- Filtreler

/** Şablonun dönem filtresi: ilk sürümde yalnız ay (docs/REPORT-DOCUMENTS-PLAN.md §7.2). */
const periodFilterSchema = z.strictObject({
  view,
  member: z.string(),
  type: z.literal('period'),
  granularity: z.literal('month').default('month'),
  /** Göreli (`-1m` geçen ay, `-1y`); yalnız adres çubuğu boşken çözülür, kopyada mutlak (`2026-09`). */
  default: z
    .string()
    .regex(/^[+-]\d+[my]$/)
    .optional(),
})

// Mevcut rapor filtreleri (cube-report.ts) aynen; belgede her filtre `view` taşır.
const documentFilterSchema = z.union([filterSchema.extend({ view }), periodFilterSchema])

// ----------------------------------------------------------------- Sorgular

/** Adlandırılmış sorgu: `value` / `change` / `condition` / `repeat` bunlara başvurur. */
const querySchema = z.strictObject({
  view,
  measures: z.array(identifier).min(1),
  dimensions: z.array(identifier).default([]),
  /** `-ölçü` azalan. */
  order: z.array(z.string()).default([]),
  where: tableWidgetSchema.shape.where,
  /** `previous`: aynı sorgu önceki dönem için de çalışır (`change`, `prev.` başvurusu). */
  compare: z.literal('previous').optional(),
  limit: z.number().int().positive().optional(),
})

// ----------------------------------------------------------------- İçerik

const href = z
  .string()
  // Göreli (şema/`//` yok) ya da `https://`. Ters bölü ve C0 denetim karakterleri tarayıcıda
  // `javascript:`/`//` kaçışına yol açar.
  .regex(
    // eslint-disable-next-line no-control-regex -- C0 karakterleri bilerek dışlanıyor
    /^(https:\/\/[^\s\\\x00-\x1f]+|(?![A-Za-z][A-Za-z0-9+.-]*:)(?!\/\/)[^\s\\\x00-\x1f]+)$/,
    'Bağlantı yalnız göreli ya da https: olabilir',
  )

/** TipTap Link `isAllowedUri` için: yalnız göreli ya da `https://`. */
export const isAllowedHref = (value: string) => href.safeParse(value).success

const markSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('bold') }),
  z.strictObject({ type: z.literal('italic') }),
  z.strictObject({
    type: z.literal('link'),
    // TipTap Link `target`, `rel`, `class` da yazar.
    attrs: z.strictObject({
      href,
      target: z.string().nullish(),
      rel: z.string().nullish(),
      class: z.null().optional(),
    }),
  }),
])

const inlineNode = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('text'),
    text: z.string().min(1),
    marks: z.array(markSchema).optional(),
  }),
  z.strictObject({ type: z.literal('hardBreak') }),
  z.strictObject({ type: z.literal('value'), attrs: z.strictObject({ of: memberRef }) }),
  z.strictObject({ type: z.literal('change'), attrs: z.strictObject({ of: memberRef }) }),
])

const inlineContent = z.array(inlineNode).optional()

const paragraph = z.strictObject({ type: z.literal('paragraph'), content: inlineContent })

const heading = z.strictObject({
  type: z.literal('heading'),
  attrs: z.strictObject({ level: z.number().int().min(1).max(3) }),
  content: inlineContent,
})

// Liste öğesi ve not yalnız metin blokları taşır (paragraf, iç içe liste); widget taşımaz.
const textBlock: z.ZodType<DocNode> = z.lazy(() =>
  z.discriminatedUnion('type', [paragraph, bulletList, orderedList]),
)
const listItem = z.strictObject({
  type: z.literal('listItem'),
  content: z.array(textBlock).min(1),
})
const bulletList = z.strictObject({
  type: z.literal('bulletList'),
  content: z.array(listItem).min(1),
})
const orderedList = z.strictObject({
  type: z.literal('orderedList'),
  // TipTap `start` (ve v3'te `type`) yazar.
  attrs: z
    .strictObject({ start: z.number().int().min(1).optional(), type: z.string().nullish() })
    .optional(),
  content: z.array(listItem).min(1),
})

// Metin tablosu (widget `reportTable` ile karışmasın): hücreler yalnız paragraf taşır.
const cellAttrs = z
  .strictObject({
    colspan: z.number().int().min(1).optional(),
    rowspan: z.number().int().min(1).optional(),
    colwidth: z.array(z.number()).nullish(),
  })
  .optional()
const cellContent = z.array(paragraph).min(1)
const tableRow = z.strictObject({
  type: z.literal('tableRow'),
  content: z
    .array(
      z.discriminatedUnion('type', [
        z.strictObject({ type: z.literal('tableHeader'), attrs: cellAttrs, content: cellContent }),
        z.strictObject({ type: z.literal('tableCell'), attrs: cellAttrs, content: cellContent }),
      ]),
    )
    .min(1),
})
const textTable = z.strictObject({
  type: z.literal('table'),
  content: z.array(tableRow).min(1),
})

/**
 * Widget düğümleri: öznitelikler cube-report.ts `widgetSchema` ile aynı; belgede sayfa
 * view'ı olmadığından `view` zorunlu. Düğüm adı `report*`: `table` metin tablosuna ait.
 */
const reportKpi = z.strictObject({
  type: z.literal('reportKpi'),
  attrs: kpiWidgetSchema.omit({ type: true }).extend({ view }),
})
const reportTable = z.strictObject({
  type: z.literal('reportTable'),
  attrs: tableWidgetSchema.omit({ type: true }).extend({ view }),
})
const reportList = z.strictObject({
  type: z.literal('reportList'),
  // Belgede liste sayfalanmaz: "en büyük 10" için `where` ve `limit` (pano `list`'inde yok).
  attrs: listWidgetSchema.omit({ type: true }).extend({
    view,
    where: tableWidgetSchema.shape.where,
    limit: z.number().int().positive().optional(),
  }),
})
const reportChart = z.strictObject({
  type: z.literal('reportChart'),
  // `range`: grafiğin dönemden bağımsız göreli aralığı (`-12m` son 12 ay).
  attrs: chartWidgetSchema
    .omit({ type: true })
    .extend({ view, range: z.string().regex(RELATIVE_RANGE_PATTERN).optional() }),
})

/** Widget düğümlerinin şemaları: editör ve çizim `view`/`where`/`limit`/`range`'i bunlardan okur. */
export const widgetNodeSchemas = { reportKpi, reportTable, reportList, reportChart }

const pagebreak = z.strictObject({ type: z.literal('pagebreak') })

const note = z.strictObject({
  type: z.literal('note'),
  attrs: z.strictObject({ type: z.enum(['info', 'warning']) }),
  content: z.array(textBlock).min(1),
})

// Blok düğümleri kendi içinde blok taşıyabilir (condition dalları, repeat): özyinelemeli.
const blockNode: z.ZodType<DocNode> = z.lazy(() =>
  z.discriminatedUnion('type', [
    paragraph,
    heading,
    bulletList,
    orderedList,
    textTable,
    reportKpi,
    reportTable,
    reportList,
    reportChart,
    condition,
    repeat,
    pagebreak,
    note,
  ]),
)

const blocks = z.array(blockNode).min(1)

/**
 * Koşul iki içerik düğümüyle modellenir: `[conditionThen, conditionElse]` (sıra sabit,
 * ikisi de zorunlu). Dönem kopyasında seçilen dalın çocukları düz içeriğe açılır.
 */
const condition = z.strictObject({
  type: z.literal('condition'),
  attrs: z.strictObject({
    left: memberRef,
    op: z.enum(['gt', 'gte', 'lt', 'lte', 'eq']),
    right: z.union([z.number(), operandRef]),
  }),
  content: z.tuple([
    z.strictObject({ type: z.literal('conditionThen'), content: blocks }),
    z.strictObject({ type: z.literal('conditionElse'), content: blocks }),
  ]),
})

/** Çok satırlı sorgunun her satırı için içeriği tekrarlar; içeride `row.ölçü` geçerlidir. */
const repeat = z.strictObject({
  type: z.literal('repeat'),
  attrs: z.strictObject({ over: identifier, limit: z.number().int().positive().default(50) }),
  content: blocks,
})

export const documentContentSchema = z.strictObject({
  type: z.literal('doc'),
  content: blocks,
})

// ------------------------------------------------------------ Başvuru kuralları

type Issue = (path: (string | number)[], message: string) => void
type Query = z.infer<typeof querySchema>
type DocumentFilter = z.infer<typeof documentFilterSchema>

/** `value`'nun `of` değeri sorgu değil, seçili dönem (`period.label`) ya da filtre (`filters.<üye>`). */
function checkReservedRef(
  ref: string,
  filters: DocumentFilter[],
  path: (string | number)[],
  issue: Issue,
) {
  const [head, member] = ref.split('.')
  if (head === 'period') {
    if (member !== 'label') return issue(path, `'${ref}': period için yalnız period.label var`)
    if (!filters.some((filter) => filter.type === 'period')) {
      issue(path, `'${ref}': şablonda period filtresi tanımlı değil`)
    }
    return
  }
  const known = filters.some(
    (filter) =>
      filter.member === member && (filter.type === 'multiSelect' || filter.type === 'boolean'),
  )
  if (!known) {
    issue(path, `'${ref}': '${member}' tanımlı bir multiSelect/boolean filtre üyesi değil`)
  }
}

const RESERVED_REFS = ['period', 'filters']

function checkRef(
  ref: string,
  kind: 'value' | 'change' | 'left' | 'right',
  repeatOver: string | undefined,
  queries: Record<string, Query>,
  path: (string | number)[],
  issue: Issue,
) {
  const parts = ref.split('.')
  if (RESERVED_REFS.includes(parts[0])) {
    return issue(path, `'${ref}': ${parts[0]}. başvurusu yalnız value düğümünde geçerli`)
  }
  const prev = parts[0] === 'prev' && parts.length === 3
  if (prev) parts.shift()
  const [head, member] = parts
  const isRow = head === 'row'
  if (isRow && !repeatOver)
    return issue(path, `'${ref}': row. başvurusu yalnız repeat içinde geçerli`)
  const name = (isRow ? repeatOver : head) as string
  const query = queries[name]
  if (!query) return issue(path, `'${ref}': '${name}' adlı sorgu tanımlı değil`)
  if (![...query.measures, ...query.dimensions].includes(member)) {
    return issue(path, `'${ref}': '${member}', '${name}' sorgusunun ölçü/boyutlarında yok`)
  }
  if ((prev || kind === 'change') && query.compare !== 'previous') {
    return issue(
      path,
      `'${ref}': önceki dönem için '${name}' sorgusunda compare: "previous" gerekir`,
    )
  }
}

function checkContent(
  node: DocNode,
  path: (string | number)[],
  repeatOver: string | undefined,
  queries: Record<string, Query>,
  filters: DocumentFilter[],
  issue: Issue,
) {
  const attrs = node.attrs ?? {}
  const attrPath = [...path, 'attrs']
  switch (node.type) {
    case 'value':
    case 'change':
      if (node.type === 'value' && RESERVED_REFS.includes((attrs.of as string).split('.')[0])) {
        checkReservedRef(attrs.of as string, filters, [...attrPath, 'of'], issue)
        break
      }
      checkRef(attrs.of as string, node.type, repeatOver, queries, [...attrPath, 'of'], issue)
      break
    case 'condition':
      checkRef(attrs.left as string, 'left', repeatOver, queries, [...attrPath, 'left'], issue)
      if (typeof attrs.right === 'string') {
        checkRef(attrs.right, 'right', repeatOver, queries, [...attrPath, 'right'], issue)
      }
      break
    case 'repeat': {
      const over = attrs.over as string
      if (repeatOver) issue(attrPath, 'repeat içinde repeat olamaz')
      if (!queries[over]) issue([...attrPath, 'over'], `'${over}' adlı sorgu tanımlı değil`)
      else if (queries[over].dimensions.length === 0) {
        issue([...attrPath, 'over'], `'${over}' çok satırlı değil: sorguda dimensions yok`)
      }
      repeatOver = over
      break
    }
  }
  node.content?.forEach((child, index) =>
    checkContent(child, [...path, 'content', index], repeatOver, queries, filters, issue),
  )
}

// ---------------------------------------------------------------- Şablon

export const reportDocumentSchema = z
  .strictObject({
    kind: z.literal('document'),
    title: z.string().min(1),
    orientation: z.enum(['portrait', 'landscape']).default('portrait'),
    filters: z.array(documentFilterSchema).default([]),
    queries: z.record(identifier, querySchema).default({}),
    content: documentContentSchema,
  })
  .superRefine((definition, ctx) => {
    const issue: Issue = (path, message) => ctx.addIssue({ code: 'custom', path, message })
    for (const name of Object.keys(definition.queries)) {
      if (RESERVED_QUERY_NAMES.includes(name)) {
        issue(['queries', name], `'${name}' ayrılmış ad, sorgu adı olamaz`)
      }
    }
    if (definition.filters.filter((filter) => filter.type === 'period').length > 1) {
      issue(['filters'], 'en fazla bir period filtresi olabilir')
    }
    checkContent(
      definition.content,
      ['content'],
      undefined,
      definition.queries,
      definition.filters,
      issue,
    )
  })
  .describe(
    'Rapor belgesi şablonu (kind: "document"). Pano türü (BI-PLAN F2.2) bu dosyaya henüz eklenmedi.',
  )

export type ReportDocument = z.infer<typeof reportDocumentSchema>

export function parseReportDocument(input: unknown): ReportDocument {
  return reportDocumentSchema.parse(input)
}

/** `schemas/report-definition.schema.json`; güncelliğini `report-document.test.ts` denetler. */
export function reportDefinitionJsonSchema() {
  return z.toJSONSchema(reportDocumentSchema, { io: 'input' })
}

// ------------------------------------------------------------- Dönem kopyası

/** `YYYY-MM`. */
export const periodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/)

/** Dondurulmuş sorgu sonucu: Cube'un düz satırları (`row['Tartim.tarih']` gibi noktalı anahtar). */
const dataRow = z.record(z.string(), z.unknown())

/**
 * `POST /api/v1/bi/reports/{id}/runs` gövdesi (docs/REPORT-DOCUMENTS-PLAN.md §5).
 * `content` açılmış belgedir (condition/repeat yok) ama aynı düğüm şemasından geçer.
 */
export const documentRunCreateSchema = z.strictObject({
  period: periodSchema,
  /** Üretimde seçilen filtreler: üye kısa adı -> değer. */
  filters: z.record(z.string(), z.union([z.string(), z.array(z.string()), z.boolean()])),
  content: documentContentSchema,
  /** Sorgu anahtarı -> satırlar. */
  data: z.record(z.string().min(1), z.array(dataRow)),
  /** Üretimdeki koşul sonucu (true = then) ve tekrar satır anahtarları; yenilemede karşılaştırılır. */
  condition_results: z.record(z.string(), z.union([z.boolean(), z.array(z.string())])),
})

export type DocumentRunCreate = z.infer<typeof documentRunCreateSchema>
